import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Timer, Save, RotateCcw, X, Plus } from "lucide-react";

export const OvertimeExemptTab = () => {
  const [levels, setLevels] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings/overtime-exempt-levels"),
      api.get("/ccnl").catch(() => ({ data: [] })),
    ]).then(([s, c]) => {
      setLevels(s.data?.levels || []);
      setDefaults(s.data?.default || []);
      const set = new Set();
      (c.data || []).forEach((ccnl) => (ccnl.level_terms || []).forEach((t) => t?.level && set.add(t.level)));
      setSuggestions([...set]);
    }).catch(() => toast.error("Errore nel caricamento")).finally(() => setLoading(false));
  }, []);

  const norm = (s) => (s || "").trim().toLowerCase();
  const add = (lv) => {
    const v = (lv || "").trim();
    if (!v) return;
    if (levels.some((x) => norm(x) === norm(v))) { toast.info("Livello già presente"); return; }
    setLevels((l) => [...l, v]);
    setInput("");
  };
  const remove = (lv) => setLevels((l) => l.filter((x) => x !== lv));

  const save = () => {
    setSaving(true);
    api.put("/settings/overtime-exempt-levels", { levels })
      .then((r) => { setLevels(r.data?.levels || []); toast.success("Livelli esenti aggiornati"); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore durante il salvataggio"))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <div data-testid="overtime-exempt-tab" className="bg-white border border-gray-200 rounded-lg p-4 text-[13px] text-gray-400">Caricamento impostazioni…</div>;
  }

  const notAdded = suggestions.filter((s) => !levels.some((x) => norm(x) === norm(s)));

  return (
    <div data-testid="overtime-exempt-tab" className="bg-white border border-gray-200 rounded-lg p-5 max-w-3xl">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-lg bg-[#16345c]/10 text-[#16345c] flex items-center justify-center"><Timer size={18} /></span>
        <div>
          <h3 className="text-[15px] font-black text-[#16345c] leading-tight">Eccezione straordinario per livello</h3>
          <p className="text-[12px] text-gray-500">Livelli di inquadramento esenti dallo straordinario nei giorni feriali.</p>
        </div>
      </div>

      <p className="text-[13px] text-gray-600 bg-[#f0f2f5] rounded-sm px-3 py-2 my-3 leading-relaxed">
        I dipendenti con uno dei livelli qui sotto <b>non generano straordinario nei giorni feriali</b> (Lun-Ven):
        anche superando le 8h la giornata resta sempre <b>Presente (P)</b>. L'eccezione <b>non si applica</b> al sabato,
        alla domenica e ai festivi infrasettimanali, dove resta la logica normale.
      </p>

      <label className="text-[12px] font-bold text-gray-700">Livelli esenti</label>
      <div className="flex flex-wrap gap-2 mt-2 mb-4 min-h-[38px]" data-testid="exempt-levels-list">
        {levels.length === 0 && <span className="text-[13px] text-gray-400 italic">Nessun livello esente</span>}
        {levels.map((lv) => (
          <span key={lv} data-testid={`exempt-chip-${lv}`} className="inline-flex items-center gap-1.5 bg-[#16345c] text-white text-[13px] font-semibold pl-3 pr-1.5 py-1.5 rounded-full">
            {lv}
            <button onClick={() => remove(lv)} data-testid={`exempt-remove-${lv}`} className="hover:bg-white/20 rounded-full p-0.5 transition-colors" aria-label={`Rimuovi ${lv}`}>
              <X size={14} />
            </button>
          </span>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(input); } }}
          placeholder="Aggiungi un livello (es. Dirigente)"
          data-testid="exempt-add-input"
          className="flex-1 max-w-xs border border-gray-300 rounded-sm px-3 py-2 text-[14px] text-[#16345c] outline-none focus:border-[#16345c] bg-white"
        />
        <button onClick={() => add(input)} data-testid="exempt-add-btn" className="flex items-center gap-1.5 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-3 py-2 rounded-sm transition-colors">
          <Plus size={15} /> Aggiungi
        </button>
      </div>

      {notAdded.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Livelli dai CCNL configurati</div>
          <div className="flex flex-wrap gap-1.5">
            {notAdded.map((s) => (
              <button key={s} onClick={() => add(s)} data-testid={`exempt-suggest-${s}`} className="text-[12px] border border-gray-300 text-gray-600 hover:border-[#16345c] hover:text-[#16345c] px-2.5 py-1 rounded-full transition-colors">
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mt-4 flex-wrap border-t border-gray-100 pt-4">
        <button onClick={save} disabled={saving} data-testid="exempt-save" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <Save size={15} /> {saving ? "Salvataggio…" : "Salva"}
        </button>
        <button onClick={() => setLevels([...defaults])} disabled={saving} data-testid="exempt-reset" className="flex items-center gap-2 border border-gray-300 text-gray-600 hover:bg-gray-100 text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <RotateCcw size={15} /> Valori predefiniti
        </button>
      </div>
    </div>
  );
};
